// Modules
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormik } from 'formik'

import { TextField } from '@dasa-health/components-react'
import { SystemBoldViewon, SystemBoldViewoff } from '@dasa-health/icons'
import { debounce } from 'lodash'
import { useLocation } from 'react-router-dom'

// Application
import { useSignUp } from 'context'
import { useSignUpAnaytics } from 'hooks/useSignUpAnaytics'
import { classNameCheck, passwordValidator, Response, userDeviceDetect, WEAK_PASSWORD_ERROR } from 'utils'
import { CreatePasswordSchema } from './utils/validation'

// Components
import { SecurityBar, SecurityBarStatus, ValidInformation } from 'components'
import { SignUpHelpTextModal } from 'redesign/components/atoms/SignUpHelpTextModal'
import { SignUpModal } from 'redesign/components/molecules/SignUpModal'
import { StyledForm, TextFieldContainer } from './styles'
import { SignUpFooter } from 'redesign/components/molecules/SignUpFooter'

export interface NewPasswordInitial {
    password: string
    confirmPassword: string
}
interface Props {
    hasMDM?: boolean
    initialValues: NewPasswordInitial
    handleSubmit: (value: NewPasswordInitial, setFieldError?: (field: string, value: string | undefined) => void) => Promise<void>
}

export const NewPassword: React.FC<Props> = ({ initialValues, handleSubmit, hasMDM = false }) => {
    const { isLoading } = useSignUp()
    const [viewPassword, setViewPassword] = useState(false)
    const [viewConfirmPassword, setConfirmViewPassword] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const [helperText, setHelperText] = useState('')
    const [valid, setValid] = useState<Response>()
    const { dispatchAnalyticsEvent } = useSignUpAnaytics({ hasMDM })
    const { isPortable } = userDeviceDetect()

    const { touched, dirty, errors, isValid, values, handleChange, handleBlur, setFieldError } = useFormik({
        initialValues,
        onSubmit: submitValues => {
            handleSubmit(submitValues, setFieldError)
        },
        validationSchema: CreatePasswordSchema,
    })
    const [variant, setVariant] = useState<string | undefined>(undefined)

    const { pathname } = useLocation()

    const [strongBarStatus, setStrongBarStatus] = useState<SecurityBarStatus | undefined>()

    const handleViewPassword = () => {
        setViewPassword(!viewPassword)
    }
    const handleViewConfirmPassword = () => {
        setConfirmViewPassword(!viewConfirmPassword)
    }

    const verifyCriteria = useCallback(() => {
        const criteriaArray: string[] = []

        valid?.lowerValid && criteriaArray.push('lower')
        valid?.upperValid && criteriaArray.push('upper')
        valid?.numberValid && criteriaArray.push('number')
        valid?.specialCharValid && criteriaArray.push('specialChar')

        if (criteriaArray.length === 0) {
            return setStrongBarStatus(undefined)
        }
        if (!valid?.lengthValid || criteriaArray.length === 1) {
            return setStrongBarStatus(SecurityBarStatus.weak)
        }
        if (criteriaArray.length === 2 || criteriaArray.length === 3) {
            return setStrongBarStatus(SecurityBarStatus.medium)
        }
        if (criteriaArray.length === 4) {
            return setStrongBarStatus(SecurityBarStatus.strong)
        }
    }, [valid])

    const handlePasswordHelperText = useCallback(() => {
        if (values.password.length > 40 && strongBarStatus === SecurityBarStatus.weak) {
            return setHelperText(WEAK_PASSWORD_ERROR)
        }
        if (errors.password) {
            return setHelperText(errors.password)
        }
        if (strongBarStatus === SecurityBarStatus.weak) {
            return setHelperText(WEAK_PASSWORD_ERROR)
        }

        return setHelperText('')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errors.password, strongBarStatus])

    const nameEvent = (pathName: string) => {
        if (pathName === '/recuperar-senha') {
            return 'reset_password'
        }
        return 'cadastro_web'
    }

    const debouncedValidate = useMemo(
        () =>
            debounce(() => {
                setVariant(
                    strongBarStatus === SecurityBarStatus.weak ? 'error' : classNameCheck(touched.password, dirty, !errors.password),
                )
                handlePasswordHelperText()
            }, 100),

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [errors.password, strongBarStatus],
    )
    useEffect(() => {
        debouncedValidate()
    }, [values, debouncedValidate])

    useEffect(() => {
        verifyCriteria()
    }, [values.password, verifyCriteria])

    useEffect(() => {
        const updated = passwordValidator(values.password)
        setValid(updated)
    }, [values])

    return (
        <StyledForm>
            <SignUpModal
                setShowModal={() => {
                    setShowModal(!showModal)
                }}
                showModal={showModal}
                title="Como criar uma senha segura?"
            >
                <p style={{ margin: 0 }}>Para criar uma senha segura você precisa incluir os requisitos abaixo:</p>
                <ul>
                    <li>Usar no mínimo 8 caracteres e no máximo 40</li>
                    <li>Usar letras maiúsculas e/ou minúsculas (ex: Ca)</li>
                    <li>Usar número e/ou caractere especial (ex: 6 ou @)</li>
                </ul>
                <p>
                    Dica: Evite informações pessoais como nome, data de nascimento e semelhantes, assim você garante uma senha mais
                    segura.
                </p>
                <h2>Por que preciso criar uma senha segura?</h2>
                <p>
                    Uma senha segura é a principal barreira para evitar que sua conta seja invadida, protegendo todos os seus dados,
                    como resultados de exames e informações de planos de saúde. Por isso, ao criar sua senha utilize todos os
                    requisitos listados.
                </p>
            </SignUpModal>

            <form
                onSubmit={e => {
                    e.preventDefault()
                    handleSubmit(values, setFieldError)
                }}
            >
                <SignUpHelpTextModal
                    data-columns="3"
                    data-current-service={pathname.includes('cadastrar') ? 'Cadastro' : 'Recuperação de senha'}
                    data-item-position="Crie uma senha"
                    data-item-text="Como criar um senha segura"
                    onClick={() => setShowModal(true)}
                >
                    Como criar uma senha segura?
                </SignUpHelpTextModal>
                <div className="password-criteria">
                    <div className="content-text-validator">
                        <ValidInformation
                            dirty={dirty}
                            text="Mínimo de 8 caracteres e no máximo 40"
                            touched={touched.password}
                            valid={valid?.lengthValid}
                        />
                        <ValidInformation
                            dirty={dirty}
                            text="Letra maiúscula e/ou minúscula"
                            touched={touched.password}
                            valid={valid?.upperOrAndLower}
                        />
                        <ValidInformation
                            dirty={dirty}
                            text="Número e/ou caractere especial"
                            touched={touched.password}
                            valid={valid?.numberOrAndSpecialChar}
                        />
                    </div>
                </div>

                <TextFieldContainer>
                    <TextField
                        className="textInput"
                        data-testid="password-input"
                        helperText={helperText}
                        id="password"
                        label="Crie uma senha"
                        maxLength={41}
                        name="password"
                        onBlur={e => {
                            handleBlur(e)
                            dispatchAnalyticsEvent('passwordOnFieldBlur', {
                                name: nameEvent(pathname),
                                password: e.target.value,
                                errorMessage: helperText,
                                funnelCurrentStep: 'Crie uma senha',
                                password_is_valid: !Object.values(passwordValidator(e.target.value)).includes(false),
                                passwordStrength: strongBarStatus,
                                passwordHidden: !viewConfirmPassword,
                            })
                        }}
                        onChange={handleChange}
                        trailingIcon={
                            viewPassword ? (
                                <SystemBoldViewoff data-testid="change-type-input" onClick={handleViewPassword} />
                            ) : (
                                <SystemBoldViewon data-testid="change-type-input" onClick={handleViewPassword} />
                            )
                        }
                        type={viewPassword ? 'text' : 'password'}
                        value={values.password}
                        variant={variant}
                    />
                    <SecurityBar
                        status={strongBarStatus}
                        styles={helperText ? { marginTop: isPortable ? '64px' : '40px' } : { marginTop: '24px' }}
                    />

                    <TextField
                        className="confirm-password textInput"
                        data-testid="confirmPassword-input"
                        disabled={touched.confirmPassword ? false : !!errors.password || !!helperText || !touched.password}
                        helperText={touched.confirmPassword && errors.confirmPassword ? errors.confirmPassword : ''}
                        id="confirmPassword"
                        label="Confirme a senha"
                        maxLength={50}
                        name="confirmPassword"
                        onBlur={e => {
                            handleBlur(e)
                            dispatchAnalyticsEvent('passwordOnFieldBlur', {
                                name: nameEvent(pathname),
                                password: e.target.value,
                                errorMessage: errors.confirmPassword,
                                funnelCurrentStep: 'Confirme a senha',
                                passwordHidden: !viewConfirmPassword,
                                password_is_valid: isValid,
                            })
                        }}
                        onChange={handleChange}
                        trailingIcon={
                            viewConfirmPassword ? (
                                <SystemBoldViewoff data-testid="change-type-input" onClick={handleViewConfirmPassword} />
                            ) : (
                                <SystemBoldViewon data-testid="change-type-input" onClick={handleViewConfirmPassword} />
                            )
                        }
                        type={viewConfirmPassword ? 'text' : 'password'}
                        value={values.confirmPassword}
                        variant={classNameCheck(touched.confirmPassword, undefined, !errors.confirmPassword)}
                    />
                </TextFieldContainer>
                <SignUpFooter
                    isSubmitButtonDisabled={!dirty || !isValid || strongBarStatus === SecurityBarStatus.weak}
                    isSubmitButtonLoading={isLoading}
                />
            </form>
        </StyledForm>
    )
}
//
